import { faCheck } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { ButtonNew as Button } from "components/buttonNew";
import { useAppSelector } from "hooks/redux";
import { WebsiteBuilderHelpIcon } from "pages/secure/facility/websiteBuilder/editor/components/helpIcon/WebsiteBuilderHelpIcon";
import { WebsiteBuilderMediaDropZone } from "pages/secure/facility/websiteBuilder/editor/components/mediaDropZone/WebsiteBuilderMediaDropZone";
import { IWebsiteBuilderSlideshowImage, WEBSITE_BUILDER_SLIDESHOW_MAX_IMAGES } from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderSlideshow";
import { isWebsiteBuilderImageAllowedFileSubtype } from "pages/secure/facility/websiteBuilder/editor/utility/websiteBuilderAllowedMedia";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { IWebsiteBuilderMediaItem } from "redux/reducers/models/websiteBuilder";

interface ISlideshowImagesAddViewProps {
  images: IWebsiteBuilderSlideshowImage[];
  selectedImages: IWebsiteBuilderSlideshowImage[];
  setSelectedImages: (selectedImages: IWebsiteBuilderSlideshowImage[]) => void;
}

type TSlideshowImagesAddViewCategory = "media" | "stock-media";

export function SlideshowImagesAddView(props: ISlideshowImagesAddViewProps) {
  const { images, selectedImages, setSelectedImages } = props;

  const { t } = useTranslation();

  const media = useAppSelector(store => store.websiteBuilderStore.editor.media);
  const stockMedia = useAppSelector(store => store.websiteBuilderStore.editor.stockMedia);
  
  const [selectedCategory, setSelectedCategory] = useState<TSlideshowImagesAddViewCategory>("media");

  const totalImagesCount = images.length + selectedImages.length;

  const mediaOptions: IWebsiteBuilderMediaItem[] = useMemo(() => {
    return media
      ?.filter(mediaItem =>
        isWebsiteBuilderImageAllowedFileSubtype(mediaItem.subtype) &&
        mediaItem.archived_at == null &&
        images.every(image => image.mediaId !== mediaItem.id),
      ) ?? [];
  }, [media, images]);

  const stockMediaOptions: IWebsiteBuilderMediaItem[] = useMemo(() => {
    return stockMedia
      ?.filter(mediaItem =>
        isWebsiteBuilderImageAllowedFileSubtype(mediaItem.subtype) &&
        images.every(image => image.mediaId !== mediaItem.id),
      ) ?? [];
  }, [stockMedia, images]);

  const selectedOptions = selectedCategory === "media" ? mediaOptions : stockMediaOptions;
  const selectedCategoryTitle = selectedCategory === "media" ? t("My media") : t("Stock images");

  function selectMediaItem(mediaItem: IWebsiteBuilderMediaItem) {
    const image: IWebsiteBuilderSlideshowImage = {
      mediaId: mediaItem.id,
      src: mediaItem.source,
      alt: "",
    };
    
    const nextSelectedImages = selectedImages.some(selectedImage => selectedImage.mediaId === image.mediaId)
      ? selectedImages.filter(selectedImage => selectedImage.mediaId !== image.mediaId)
      : [...selectedImages, image];

    setSelectedImages(nextSelectedImages);
  }

  return (
    <div className="website-builder-slideshow-images-add">
      <div className="slideshow-images-add-header">
        <div className="slideshow-images-add-selection">
          <div className="slideshow-images-add-selection-message">{t("{{selectedImagesCount}} selected", { selectedImagesCount: selectedImages.length })}</div>
          <Button type="text" onClick={() => setSelectedImages([])}>
            {t("Deselect all")}
          </Button>
        </div>
        <div className="slideshow-images-add-total">
          <div>
            {t("{{totalImagesCount}} of {{maxImages}} images", {
              totalImagesCount,
              maxImages: WEBSITE_BUILDER_SLIDESHOW_MAX_IMAGES,
            })}
          </div>
          <WebsiteBuilderHelpIcon
            id="website-builder-slideshow-images-add-total-help"
            message={t("You can only add up to {{maxImages}} images in this slideshow.", {
              maxImages: WEBSITE_BUILDER_SLIDESHOW_MAX_IMAGES,
            })}
            placement="top"
          />
        </div>
      </div>

      <div className="slideshow-images-add-content">
        <div className="slideshow-images-add-sidebar">
          <WebsiteBuilderMediaDropZone
            theme="dark"
            condensed
            allowImages
          />

          <div className="slideshow-images-add-categories">
            <button
              type="button"
              className={selectedCategory === "media" ? "active" : null}
              onClick={() => setSelectedCategory("media")}
            >
              <div>{t("My images")}</div>
              <div className="slideshow-images-add-categories-count">{mediaOptions.length}</div>
            </button>
            <button
              type="button"
              className={selectedCategory === "stock-media" ? "active" : null}
              onClick={() => setSelectedCategory("stock-media")}
            >
              <div>{t("Stock images")}</div>
              <div className="slideshow-images-add-categories-count">{stockMediaOptions.length}</div>
            </button>
          </div>
        </div>

        <div className="slideshow-images-add-library">
          <div className="slideshow-images-add-library-title">{selectedCategoryTitle}</div>
          {selectedOptions.length === 0 ? (
            <div className="slideshow-images-add-library-empty-message">{t("No images available")}</div>
          ) : (
            <div
              className={classNames("slideshow-images-add-library-options", {
                "options-maximum-reached": totalImagesCount >= WEBSITE_BUILDER_SLIDESHOW_MAX_IMAGES,
              })}
            >
              {selectedOptions.map(mediaItem => (
                <div
                  key={mediaItem.id}
                  className={classNames("slideshow-images-add-library-option", {
                    "option-selected": selectedImages.some(selectedImage => selectedImage.mediaId === mediaItem.id),
                  })}
                  onClick={() => selectMediaItem(mediaItem)}
                >
                  <img
                    src={mediaItem.source}
                    draggable={false}
                  />
                  <div className="slideshow-images-add-library-option-checkbox">
                    <FontAwesomeIcon
                      className="slideshow-images-add-library-option-checkbox-check"
                      icon={faCheck}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
